/* eslint-disable no-unused-expressions, class-methods-use-this */
import SelectorMapping from '../SelectorMapping';

class EventHandler {
  eventMapping = {
    'google-search:no-results': null,
    'google-search:results-loaded': this.resultsLoaded,
    'google-search:page-updated': this.pageUpdated,
    'google-search:sort-updated': this.sortUpdated,
    'google-search:search-submitted': this.searchSubmitted,
    'google-search:recaptcha': this.recaptcha,
    'google-search:search-reset': this.reset,
  };

  constructor(GoogleSearchControllerInstance) {
    this.controller = GoogleSearchControllerInstance;

    this.attachListeners();
  }

  async searchSubmitted(event) {
    event.preventDefault();

    const { controller } = this;
    const { FormHandler } = controller;

    const searchString = FormHandler.searchValue;

    if (!searchString) {
      return;
    }

    await controller.forwardSearchToGoogle(searchString);
  }

  pageUpdated() {
    const { controller } = this;

    controller.PaginationHandler.setPagination();
    controller.removeTimestampFromResults();
  }

  sortUpdated(event) {
    const { controller } = this;

    const sortValue = event.detail;
    controller.SortHandler.apply(sortValue);
  }

  reset() {
    const { href } = window.location;
    const defragmented = href.replace(/#.+/, '');

    window.location = defragmented;
  }

  async resultsLoaded() {
    const { controller } = this;

    const [resultsContainerSelector, resultCountSelector] = SelectorMapping.get(
      'gsc-results__container',
      'gsc-results__result-count',
    );

    if (!resultsContainerSelector || !resultCountSelector) {
      console.error('Missing required selectors for results loaded handler');
      return;
    }

    const resultsContainer = controller.$container.querySelector(
      resultsContainerSelector,
    );

    if (!resultsContainer) {
      console.error('Results container element not found');
      return;
    }

    const resultCountContainer = controller.$container.querySelector(resultCountSelector);
    if (resultCountContainer) {
      // Store the current text content to compare against
      const currentText = resultCountContainer.innerText || '';

      controller.GoogleObserver.listenTo(
        resultCountContainer,
        (mutations, observer) => {
          // Only process if text has actually changed to avoid infinite loops
          if (
            resultCountContainer.innerText
            && resultCountContainer.innerText !== currentText
          ) {
            controller.removeTimestampFromResults();

            // Disconnects this specific observer after first meaningful change
            // This prevents continuous triggering
            observer.disconnect();

            // Remove this element from the listening list in GoogleObserver
            const index = controller.GoogleObserver.listeningTo.indexOf(
              resultCountContainer,
            );
            if (index > -1) {
              controller.GoogleObserver.listeningTo.splice(index, 1);
            }
          }
        },
        {
          attributes: false,
          childList: true,
          subtree: true,
          characterData: true,
        },
      );
    }

    const hasPosts = resultsContainer.textContent.indexOf('www.transwest.com') > 0;

    controller.$container.dataset.compGoogleSearchNoResults = !hasPosts;

    controller.removeTimestampFromResults();

    controller.PaginationHandler.setPagination();
    controller.SortHandler.replaceGoogleSortForm();
    controller.showSearchResults();
    controller.searchStatus = 'loaded';

    if (!controller.resultsRendered) {
      controller.resultsRendered = true;
    }
  }

  async recaptcha() {
    const { controller } = this;

    const resultsEl = document.querySelector('.gsc-wrapper');
    const recaptchaEl = document.querySelector(
      'div.gs-webResult.gs-captcha-outer-wrapper',
    );

    if (!recaptchaEl || !resultsEl) {
      console.error('Recaptcha or results elements not found');
      return;
    }

    controller.$container.appendChild(recaptchaEl);

    try {
      await controller.GoogleObserver.watchFor(controller.$container, () => {
        const el = controller.$container.querySelector(
          'div.gs-webResult.gs-captcha-outer-wrapper',
        );

        return el === null;
      });
    } catch (error) {
      controller.handleError(error);
    }

    resultsEl.appendChild(recaptchaEl);
  }

  attachListeners() {
    Object.entries(this.eventMapping).forEach((entry) => {
      const [eventName, eventHandler] = entry;

      if (!eventHandler) {
        return;
      }

      window.addEventListener(eventName, async (event) => {
        // bind(this) ensures we always have access to this EventHandler instance
        await eventHandler.bind(this)(event);
      });
    });
  }
}

export default EventHandler;
